// src/pages/PracticePage.jsx - COMPLETE REDESIGN with 3Ps hover effects
import React, { useMemo, useState, useEffect } from 'react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { FileText, BookOpen, Sparkles, Headphones, Edit, Mic, Target, Zap, ArrowLeft, BarChart3, MessageSquare, Flame, TrendingUp, Award, Home, GraduationCap, ChevronRight, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUserStats } from '../hooks/useUserStats';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';
import RecommendedSessionBanner from '../components/practice/RecommendedSessionBanner';
import enhancedUserService from '../services/enhancedUserService';
import { trackEvent } from '../firebase';

const PracticePage = ({ examData, user, onBack, personalizedPlan: personalizedPlanProp }) => {
    const { t } = useTranslation();
    const navigate = useLocalizedNavigate();
    const isExamSelected = !!examData;
    const { userStats, loading: statsLoading } = useUserStats();
    const [isBackHovered, setIsBackHovered] = useState(false);

    // Derive purpose to order sections (exam → exam sections first; training/both → language skills first)
    // Priority: plan prop → localStorage plan → Firestore user profile
    const [profilePurpose, setProfilePurpose] = useState(null);
    useEffect(() => {
        if (!user?.uid) return;
        let cancelled = false;
        enhancedUserService.getOnboardingComplete(user.uid).then(onboardingData => {
            if (!cancelled && onboardingData?.purpose) setProfilePurpose(onboardingData.purpose);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [user?.uid]);

    const purpose = useMemo(() => {
        if (personalizedPlanProp?.purpose) return personalizedPlanProp.purpose;
        try {
            const raw = localStorage.getItem(`personalizedPlan_${user?.uid}`) || localStorage.getItem('personalizedPlan');
            const p = raw ? JSON.parse(raw)?.purpose : null;
            if (p) return p;
        } catch { /* ignore */ }
        return profilePurpose;
    }, [personalizedPlanProp, user?.uid, profilePurpose]);

    const isExamMode = purpose === 'exam';
    const isTrainingMode = purpose === 'training';

    // Use plan from props, or fallback to localStorage (e.g. before App has finished loading it)
    const personalizedPlan = useMemo(() => {
        if (personalizedPlanProp?.weeks?.length) return personalizedPlanProp;
        if (!user?.uid) return null;
        try {
            const raw = localStorage.getItem(`personalizedPlan_${user.uid}`) || localStorage.getItem('personalizedPlan');
            const plan = raw ? JSON.parse(raw) : null;
            return plan?.weeks?.length ? plan : null;
        } catch {
            return null;
        }
    }, [personalizedPlanProp, user?.uid]);

    const computedStats = useMemo(() => {
        const fallbackStats = {
            dailyStats: { lessonsToday: 0, currentStreak: 0 },
            weeklyStats: { totalXP: 0 },
            allTimeStats: { totalSessions: 0 }
        };

        if (!userStats) return fallbackStats;

        const stats = userStats.profile?.stats || userStats.stats || {};
        const profile = userStats.profile || userStats;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const sessionsToday = userStats.recentSessions?.filter(session => {
            if (!session.startTime) return false;
            const sessionDate = new Date(session.startTime);
            const sessionDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
            return sessionDay.getTime() === today.getTime();
        }) || [];

        return {
            dailyStats: {
                lessonsToday: sessionsToday.length || Math.min(stats.vocabularysCompleted || 0, 5),
                currentStreak: stats.currentStreak || stats.studyStreak || 0
            },
            weeklyStats: {
                totalXP: Math.min(stats.examsPassed * 10 || 0, 500)
            },
            allTimeStats: {
                totalSessions: Math.max(stats.vocabularysCompleted || 0, stats.grammarExercisesCompleted || 0)
            }
        };
    }, [userStats]);

    const skillBasedOptions = useMemo(() => [
        {
            id: 'vocabulary',
            icon: <BookOpen className="w-8 h-8" />,
            title: t('practice.options.vocabulary.title'),
            description: t('practice.options.vocabulary.descriptionShort'),
            path: '/practice/vocabulary',
            gradient: 'from-blue-500 to-indigo-600'
        },
        {
            id: 'grammar',
            icon: <Target className="w-8 h-8" />,
            title: t('practice.options.grammar.title'),
            description: t('practice.options.grammar.descriptionShort'),
            path: '/practice/grammar',
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            id: 'redemittel',
            icon: <MessageSquare className="w-8 h-8" />,
            title: t('practice.options.redemittel.title'),
            description: t('practice.options.redemittel.descriptionShort'),
            path: '/practice/redemittel',
            gradient: 'from-purple-500 to-violet-600'
        }
    ], [t]);

    const examOptions = useMemo(() => [
        {
            id: 'reading',
            icon: <BookOpen className="w-8 h-8" />,
            title: t('practice.options.reading.title'),
            description: t('practice.options.reading.descriptionShort'),
            path: '/practice/reading',
            gradient: 'from-indigo-500 to-blue-600'
        },
        {
            id: 'listening',
            icon: <Headphones className="w-8 h-8" />,
            title: t('practice.options.listening.title'),
            description: t('practice.options.listening.descriptionShort'),
            path: '/practice/listening',
            gradient: 'from-blue-500 to-cyan-600'
        },
        {
            id: 'writing',
            icon: <FileText className="w-8 h-8" />,
            title: t('practice.options.writing.title'),
            description: t('practice.options.writing.descriptionShort'),
            path: '/practice/writing',
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            id: 'speaking',
            icon: <Mic className="w-8 h-8" />,
            title: t('practice.options.speaking.title'),
            description: t('practice.options.speaking.descriptionShort'),
            path: '/practice/exam-section?sectionId=speaking',
            gradient: 'from-red-500 to-pink-600',
            comingSoon: true
        }
    ], [t]);

    return (
        <div className="min-h-screen bg-white relative overflow-x-hidden overflow-y-auto">
            <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 sm:pb-8">
                {/* Header, compact on mobile */}
                <div className="mb-4 sm:mb-12">
    {/* Header row with back button and centered badge */}
    <div className="relative flex items-center justify-center mb-3 sm:mb-6">
        {/* Back button - hidden on mobile (moved to bottom nav) */}
        <button
            onClick={onBack}
            onMouseEnter={() => setIsBackHovered(true)}
            onMouseLeave={() => setIsBackHovered(false)}
            className="absolute left-0 hidden sm:inline-flex items-center gap-2 px-3 py-2 min-h-[44px] bg-white rounded-lg border-2 border-black overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
        >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                right: 0,
                                width: isBackHovered ? '100%' : '0%',
                                background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
                                transition: 'width 0.7s ease-out',
                                zIndex: 0
                            }}
                        />
                        <ArrowLeft
                            className="w-5 h-5 relative z-10 transition-colors duration-500"
                            style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
                        />
                        <span
                            className="font-semibold relative z-10 transition-colors duration-500"
                            style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
                        >
                            Back
                        </span>
                    </button>

                   <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white text-[11px] sm:text-sm font-bold tracking-widest uppercase rounded-full">
            {t('practice.title')}
        </div>

        {/* Stats badges - top right corner */}
        {user && (
          <div className="absolute right-0 hidden sm:block">
            <div className="relative bg-white rounded-2xl border-2 border-black/10 overflow-hidden">
              <div className="relative flex items-stretch">
                {/* Today */}
                <div className="flex flex-col items-center justify-center py-4 px-4 gap-1.5 min-h-[56px]">
                  <BookOpen className="w-5 h-5 text-black/30" />
                  <p className="font-space text-lg font-black text-black leading-none">{statsLoading ? "..." : computedStats.dailyStats.lessonsToday}</p>
                  <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">Today</p>
                </div>

                <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />

                {/* Streak */}
                <div className="flex flex-col items-center justify-center py-4 px-4 gap-1.5 min-h-[56px]">
                  <Flame className="w-5 h-5 text-black/30" />
                  <p className="font-space text-lg font-black text-black leading-none">{statsLoading ? "..." : computedStats.dailyStats.currentStreak}</p>
                  <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">Streak</p>
                </div>

                <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />

                {/* Weekly XP */}
                <div className="flex flex-col items-center justify-center py-4 px-4 gap-1.5 min-h-[56px]">
                  <Zap className="w-5 h-5 text-black/30" />
                  <p className="font-space text-lg font-black text-black leading-none">{statsLoading ? "..." : computedStats.weeklyStats.totalXP}</p>
                  <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">XP</p>
                </div>

                <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />

                {/* Sessions */}
                <div className="flex flex-col items-center justify-center py-4 px-4 gap-1.5 min-h-[56px]">
                  <Target className="w-5 h-5 text-black/30" />
                  <p className="font-space text-lg font-black text-black leading-none">{statsLoading ? "..." : computedStats.allTimeStats.totalSessions}</p>
                  <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">Sessions</p>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>

                    {/* When user has a study plan, the banner replaces "Choose Your Practice"; otherwise show the title */}
                    {!personalizedPlan && (
                        <>
                            <h1 className="font-space text-2xl sm:text-5xl md:text-6xl font-black text-black mb-2 sm:mb-4 tracking-tight leading-none">
                                Choose Your Practice
                            </h1>
                            <p className="text-sm sm:text-xl text-black/60 max-w-3xl">
                                {examData ? `${examData.examProvider} ${examData.level}` : t('practice.noExamSelected')}
                            </p>
                        </>
                    )}
                </div>

                {/* Recommended Session Banner, replaces "Choose Your Practice" when user has a study plan */}
                {personalizedPlan && (
                    <RecommendedSessionBanner
                        personalizedPlan={personalizedPlan}
                        examData={examData}
                    />
                )}

                {/* Stats. Unified strip on mobile, individual cards on desktop */}
                {user && (
                    <>
                        {/* MOBILE: White unified stats strip with gradient dividers */}
                        <div className="sm:hidden mb-4" style={{display: 'none'}}>
                            <div className="relative bg-white rounded-2xl border-2 border-black/10 overflow-hidden">
                                <div className="relative flex items-stretch">
                                    {/* Today */}
                                    <div className="flex-1 flex flex-col items-center justify-center py-3.5 px-1 gap-1">
                                        <BookOpen className="w-4 h-4 text-black/30" />
                                        <p className="font-space text-xl font-black text-black leading-none">{statsLoading ? "..." : computedStats.dailyStats.lessonsToday}</p>
                                        <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">Today</p>
                                    </div>

                                    <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />

                                    {/* Streak */}
                                    <div className="flex-1 flex flex-col items-center justify-center py-3.5 px-1 gap-1">
                                        <Flame className="w-4 h-4 text-black/30" />
                                        <p className="font-space text-xl font-black text-black leading-none">{statsLoading ? "..." : computedStats.dailyStats.currentStreak}</p>
                                        <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">Streak</p>
                                    </div>

                                    <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />

                                    {/* Weekly XP */}
                                    <div className="flex-1 flex flex-col items-center justify-center py-3.5 px-1 gap-1">
                                        <Zap className="w-4 h-4 text-black/30" />
                                        <p className="font-space text-xl font-black text-black leading-none">{statsLoading ? "..." : computedStats.weeklyStats.totalXP}</p>
                                        <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">XP</p>
                                    </div>

                                    <div className="w-px self-stretch my-3" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.12), transparent)' }} />

                                    {/* Sessions */}
                                    <div className="flex-1 flex flex-col items-center justify-center py-3.5 px-1 gap-1">
                                        <Target className="w-4 h-4 text-black/30" />
                                        <p className="font-space text-xl font-black text-black leading-none">{statsLoading ? "..." : computedStats.allTimeStats.totalSessions}</p>
                                        <p className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">Sessions</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* DESKTOP: Original rich individual cards - hidden, showing in header instead */}
                        <div className="hidden gap-4 mb-16">
                            {/* Lessons Today - White */}
                            <div className="group relative bg-white rounded-2xl border-[3px] border-black/15 hover:border-[#3A86FF] transition-all duration-500 overflow-hidden hover:shadow-2xl touch-manipulation">
                                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                                <div className="relative z-10 p-6 flex flex-row items-center justify-between w-full h-full">
                                    <div className="flex flex-col items-start gap-3">
                                        <BookOpen className="w-8 h-8 text-[#3A86FF] group-hover:scale-110 transition-transform" />
                                        <p className="text-sm text-black/50 font-medium uppercase tracking-wider leading-tight">Today</p>
                                    </div>
                                    <p className="font-space text-4xl font-black text-black">{statsLoading ? "..." : computedStats.dailyStats.lessonsToday}</p>
                                </div>
                                <BookOpen className="absolute -bottom-4 -right-4 w-24 h-24 text-black opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                            </div>

                            {/* Streak - Black */}
                            <div className="group relative bg-black text-white rounded-2xl border-[3px] border-black transition-all duration-500 overflow-hidden hover:shadow-2xl hover:scale-105 touch-manipulation">
                                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-white/20" />
                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-white/20" />
                                <div className="relative z-10 p-6 flex flex-row items-center justify-between w-full h-full">
                                    <div className="flex flex-col items-start gap-3">
                                        <Flame className="w-8 h-8 text-orange-400 group-hover:scale-110 transition-transform" />
                                        <p className="text-sm text-white/50 font-medium uppercase tracking-wider leading-tight">Streak</p>
                                    </div>
                                    <p className="font-space text-4xl font-black">{statsLoading ? "..." : computedStats.dailyStats.currentStreak}</p>
                                </div>
                                <Flame className="absolute -bottom-4 -right-4 w-24 h-24 text-white opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-orange-400 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                            </div>

                            {/* Weekly XP - White */}
                            <div className="group relative bg-white rounded-2xl border-[3px] border-black/15 hover:border-[#3A86FF] transition-all duration-500 overflow-hidden hover:shadow-2xl touch-manipulation">
                                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                                <div className="relative z-10 p-6 flex flex-row items-center justify-between w-full h-full">
                                    <div className="flex flex-col items-start gap-3">
                                        <Zap className="w-8 h-8 text-[#3A86FF] group-hover:scale-110 transition-transform" />
                                        <p className="text-sm text-black/50 font-medium uppercase tracking-wider leading-tight">Weekly XP</p>
                                    </div>
                                    <p className="font-space text-4xl font-black text-black">{statsLoading ? "..." : computedStats.weeklyStats.totalXP}</p>
                                </div>
                                <Zap className="absolute -bottom-4 -right-4 w-24 h-24 text-black opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                            </div>

                            {/* Total Sessions - Royal Blue */}
                            <div className="group relative bg-gradient-to-br from-[#3A86FF] to-blue-700 text-white rounded-2xl border-[3px] border-[#3A86FF] transition-all duration-500 overflow-hidden hover:shadow-2xl hover:scale-105 touch-manipulation">
                                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-white/30" />
                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-white/30" />
                                <div className="relative z-10 p-6 flex flex-row items-center justify-between w-full h-full">
                                    <div className="flex flex-col items-start gap-3">
                                        <Target className="w-8 h-8 group-hover:scale-110 transition-transform" />
                                        <p className="text-sm text-white/60 font-medium uppercase tracking-wider leading-tight">Sessions</p>
                                    </div>
                                    <p className="font-space text-4xl font-black">{statsLoading ? "..." : computedStats.allTimeStats.totalSessions}</p>
                                </div>
                                <Target className="absolute -bottom-4 -right-4 w-24 h-24 text-white opacity-10 pointer-events-none group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                            </div>
                        </div>
                    </>
                )}

                {/* Sections ordered by purpose: exam mode → exam first; training/both → language skills first */}
                <div className="flex flex-col gap-4 sm:gap-16">

                {/* Language Skills Section */}
                <div className={`${!isTrainingMode ? 'order-2' : 'order-1'}`}>
                    <h2 className="font-space text-xl sm:text-3xl font-black text-black mb-3 sm:mb-8 tracking-tight">
                        Language Skills
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
                        {skillBasedOptions.map((option, index) => (
                            <div
                                key={option.id}
                                onClick={() => {
                                    trackEvent('practice_section_opened', { section: option.id, section_type: 'skill' });
                                    navigate(option.path);
                                }}
                                className="ps-card-animated p-4 sm:p-8 rounded-2xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-white text-black cursor-pointer group touch-manipulation active:scale-[0.98] active:shadow-inner transition-all duration-300"
                            >
                                <div className="ps-number-bg absolute top-2 sm:top-4 right-3 sm:right-6 font-space text-[3rem] sm:text-[5rem] font-black text-[#3A86FF] opacity-15 leading-none">
                                    0{index + 1}
                                </div>

                                <div className="ps-decorative-dot ps-dot-1"></div>
                                <div className="ps-decorative-dot ps-dot-2"></div>
                                <div className="ps-decorative-dot ps-dot-3"></div>

                                <div className="ps-shine-effect"></div>

                                {/* Mobile: horizontal row. Desktop: stacked */}
                                <div className="flex items-center gap-3 sm:block">
                                    <div className="ps-icon-animated relative w-[44px] h-[44px] sm:w-[70px] sm:h-[70px] bg-[#3A86FF] rounded-xl sm:rounded-2xl flex items-center justify-center text-white flex-shrink-0 sm:mb-6">
                                        {React.cloneElement(option.icon, { className: 'w-6 h-6 sm:w-8 sm:h-8' })}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h3 className="ps-title-animated font-space text-lg sm:text-3xl font-black text-[#3A86FF] mb-0.5 sm:mb-3">
                                            {option.title}
                                        </h3>
                                        <p className="ps-desc-animated leading-relaxed text-sm sm:text-base text-black/70 line-clamp-1 sm:line-clamp-none">
                                            {option.description}
                                        </p>
                                    </div>

                                    <ChevronRight className="w-5 h-5 text-black/30 flex-shrink-0 sm:hidden" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Exam Sections */}
                <div className={`${!isTrainingMode ? 'order-1' : 'order-2'} mb-4 sm:mb-0`}>
                    <h2 className="font-space text-xl sm:text-3xl font-black text-black mb-3 sm:mb-8 tracking-tight">
                        Exam Sections
                    </h2>

                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                        {examOptions.map((option, index) => (
                            <div
                                key={option.id}
                                onClick={() => {
                                    if (!option.comingSoon) {
                                        trackEvent('practice_section_opened', { section: option.id, section_type: 'exam' });
                                        navigate(option.path);
                                    }
                                }}
                                className={`ps-card-animated p-3 sm:p-8 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-white text-black touch-manipulation ${option.comingSoon ? 'opacity-75 ps-card-coming-soon cursor-not-allowed' : 'cursor-pointer group active:scale-[0.98] active:shadow-inner transition-all duration-300'}`}
                            >
                                <div className="ps-decorative-dot ps-dot-1 hidden sm:block"></div>
                                <div className="ps-decorative-dot ps-dot-2 hidden sm:block"></div>
                                <div className="ps-decorative-dot ps-dot-3 hidden sm:block"></div>

                                <div className="ps-shine-effect"></div>

                                <div className="ps-icon-animated relative w-[40px] h-[40px] sm:w-[60px] sm:h-[60px] bg-[#3A86FF] rounded-xl sm:rounded-2xl flex items-center justify-center text-white mb-2 sm:mb-6">
                                    {React.cloneElement(option.icon, { className: 'w-5 h-5 sm:w-8 sm:h-8' })}
                                </div>

                                <h3 className="ps-title-animated font-space text-sm sm:text-2xl font-black text-[#3A86FF] mb-1 sm:mb-3">
                                    {option.title}
                                </h3>

                                <p className="ps-desc-animated leading-relaxed text-xs sm:text-sm text-black/70 mb-2 sm:mb-3 line-clamp-2 sm:line-clamp-none">
                                    {option.description}
                                </p>

                                {option.comingSoon && (
                                    <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-white bg-black rounded-full pointer-events-none">
                                        <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                        {t('message.comingSoon')}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                </div>{/* end purpose-ordered sections flex container */}
            </div>

            {/* Bottom Navigation. Practice Page */}
            <GrammarBottomNav
                buttons={[
                    {
                        id: 'home',
                        icon: Home,
                        label: t('nav.home', { defaultValue: 'Home' }),
                        onClick: () => navigate('/dashboard'),
                    },
                    {
                        id: 'lessons',
                        icon: GraduationCap,
                        label: t('nav.lessons', { defaultValue: 'Lessons' }),
                        onClick: () => navigate('/lessons'),
                    },
                    {
                        id: 'profile',
                        icon: User,
                        label: t('nav.profile', { defaultValue: 'Profile' }),
                        onClick: () => navigate('/profile'),
                    }
                ]}
            />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

                .font-space {
                    font-family: 'Space Grotesk', sans-serif;
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(5deg); }
                }

                .animate-float {
                    animation: float 20s ease-in-out infinite;
                }

                .ps-card-animated {
                    position: relative;
                    overflow: hidden;
                    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                }

                @media (hover: hover) {
                    .ps-card-animated:hover {
                        transform: scale(1.05) translateY(-8px);
                        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
                        background: linear-gradient(to bottom right, #000, #1a1a1a);
                        color: white;
                    }

                    .ps-card-animated:hover .ps-title-animated,
                    .ps-card-animated:hover .ps-desc-animated {
                        color: white;
                    }

                    .ps-card-animated:hover .ps-icon-animated {
                        background: white;
                        color: #3A86FF;
                        transform: scale(1.1) rotate(-6deg);
                    }

                    .ps-card-coming-soon:hover {
                        transform: none;
                        box-shadow: none;
                        background: white;
                        color: black;
                    }

                    .ps-card-coming-soon:hover .ps-title-animated,
                    .ps-card-coming-soon:hover .ps-desc-animated {
                        color: black;
                    }

                    .ps-card-coming-soon:hover .ps-icon-animated {
                        background: #3A86FF;
                        color: white;
                        transform: none;
                    }

                    .ps-card-coming-soon:hover .ps-decorative-dot {
                        opacity: 0;
                    }

                    .ps-card-coming-soon:hover .ps-shine-effect {
                        left: -100%;
                    }

                    .ps-card-animated:hover .ps-number-bg {
                        opacity: 0.25 !important;
                        color: white;
                    }

                    .ps-card-animated:hover .ps-decorative-dot {
                        opacity: 1;
                        animation: particlePulse 2s ease-in-out infinite;
                    }

                    .ps-card-animated:hover .ps-dot-1 { animation-delay: 0s; }
                    .ps-card-animated:hover .ps-dot-2 { animation-delay: 0.3s; }
                    .ps-card-animated:hover .ps-dot-3 { animation-delay: 0.6s; }

                    .ps-card-animated:hover .ps-shine-effect {
                        left: 100%;
                    }
                }

                .ps-number-bg {
                    position: absolute;
                    transition: all 0.5s ease;
                }

                .ps-decorative-dot {
                    position: absolute;
                    width: 8px;
                    height: 8px;
                    background: #3A86FF;
                    border-radius: 50%;
                    opacity: 0;
                    transition: all 0.5s ease;
                }

                .ps-dot-1 { top: 16px; right: 16px; }
                .ps-dot-2 { bottom: 16px; left: 16px; }
                .ps-dot-3 { top: 50%; right: 24px; }

                @keyframes particlePulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.5); opacity: 0.5; }
                }

                .ps-shine-effect {
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                    transition: left 0.7s ease;
                }<div className="mb-12">

                .ps-icon-animated,
                .ps-title-animated,
                .ps-desc-animated {
                    position: relative;
                    z-index: 10;
                    transition: all 0.5s ease;
                }
            `}</style>
        </div>
    );
};

export default PracticePage;